import { computed, ref, toRef, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import { useBuildingBlockStore } from '@/stores/buildingBlock'
import type { BuildingBlockDto, MaterialDto, ProductDto } from '@/stores/buildingBlock'
import { useMaterialStore } from '@/stores/material'
import { useActivityStore } from '@/stores/activity'
import { useQuantityBuildingBlockStore } from '@/stores/quantityBuildingBlock'
import { useTransportConfigStore } from '@/stores/transportConfig'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import { useBuildingBlockDeletion } from '@/components/building-block-form/composable/useBuildingBlockDeletion'
import { useBuildingBlockStatus } from '@/components/building-block-form/composable/useBuildingBlockStatus'
import type { BuildingBlockCardProps } from '@/components/building-block-form/type/type'
import {
  buildChangedDetails,
  buildMaterialNamesById,
  buildMissingDetails,
  resolveIssueBadgeColor,
  resolveIssueClass,
  resolveIssueTitle
} from '@/components/building-block-form/utils/buildingBlockCardUtils'
import {
  DEFAULT_CONVERSION_FACTOR,
  PERMANENT_LIFETIME
} from '@/components/building-block-form/constants/buildingBlockDefaults'
import {
  DEFAULT_BUILDING_BLOCK_TYPE,
  getBuildingBlockTypeCopy
} from '@/components/building-block-form/constants/buildingBlockTypeCopy'
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce'
import { ISSUE_SEVERITIES } from '@/constants/issueSeverity'

export function useBuildingBlockCard(props: BuildingBlockCardProps) {
  type AutosaveMessageColor = 'default' | 'success' | 'error'

  const route = useRoute()
  const block = toRef(props, 'block')

  const buildingBlockStore = useBuildingBlockStore()
  const materialStore = useMaterialStore()
  const activityStore = useActivityStore()
  const quantityBuildingBlockStore = useQuantityBuildingBlockStore()
  const transportConfigStore = useTransportConfigStore()
  const confirmDeleteStore = useConfirmDeleteStore()
  const { deleteBuildingBlock } = useBuildingBlockDeletion()
  const { items: materialItems } = storeToRefs(materialStore)

  const calculationId = computed(() =>
    Number(route.params.calculationId || block.value.calculation)
  )
  const materialNamesById = computed(() =>
    buildMaterialNamesById(
      materialItems.value.map((material) => ({ id: material.id, name: material.name }))
    )
  )

  const blockType = computed(() => block.value.block_type ?? DEFAULT_BUILDING_BLOCK_TYPE)
  const isEquipmentBlock = computed(() => blockType.value === 'equipment')
  const blockTypeCopy = computed(() => getBuildingBlockTypeCopy(blockType.value))
  const impactLabel = computed(() => blockTypeCopy.value.impactLabel)

  const { run, activeAutosaveBlockId, autosaveMessageState } = useBuildingBlockStatus()

  const isActiveAutosaveCard = computed(() => activeAutosaveBlockId.value === block.value.id)

  const autosaveMessage = computed(() => {
    if (!isActiveAutosaveCard.value) return ''
    if (autosaveMessageState.value === 'saved') return 'Changes saved'
    if (autosaveMessageState.value === 'failed') return 'Changes could not be saved'
    return ''
  })
  const autosaveMessageColor = computed<AutosaveMessageColor>(() => {
    if (!isActiveAutosaveCard.value) return 'default'
    if (autosaveMessageState.value === 'saved') return 'success'
    if (autosaveMessageState.value === 'failed') return 'error'
    return 'default'
  })

  const blockMaterials = computed(() =>
    materialItems.value.filter((material) => material.block === block.value.id)
  )

  const blockTotal = computed(() => {
    return blockMaterials.value.reduce((totalImpact, material) => {
      return totalImpact + (material.impact_calculation?.total ?? 0)
    }, 0)
  })

  const issueClass = computed(() => resolveIssueClass(props.switchValidationIssue))
  const issueBadgeColor = computed(() => resolveIssueBadgeColor(props.switchValidationIssue))
  const issueTitle = computed(() => resolveIssueTitle(props.switchValidationIssue))
  const isEditLockedByMissingIssue = computed(
    () => props.switchValidationIssue?.severity === ISSUE_SEVERITIES.MISSING
  )
  const missingMaterialIds = computed(() => {
    const materialIds = new Set<number>()

    for (const row of props.switchValidationIssue?.missingRows ?? []) {
      materialIds.add(Number(row.material_id))
    }

    return materialIds
  })
  const missingDetails = computed(() =>
    buildMissingDetails(props.switchValidationIssue, materialNamesById.value)
  )
  const changedDetails = computed(() =>
    buildChangedDetails(props.switchValidationIssue, materialNamesById.value)
  )

  const showAddProduct = ref(false)

  const buildingBlockNameField = {
    type: 'string',
    required: false,
    read_only: false,
    label: 'Building block',
    max_length: 255,
    field_type: 'text',
    parameter: 'name'
  }
  const buildingBlockUnitField = {
    type: 'string',
    required: false,
    read_only: false,
    label: 'Unit',
    max_length: 255,
    field_type: 'text',
    parameter: 'unit'
  }

  const editingBlock = ref({
    name: block.value.name,
    unit: block.value.unit
  })

  watch(
    () => block.value.name,
    (value) => {
      editingBlock.value.name = value
    }
  )
  watch(
    () => block.value.unit,
    (value) => {
      editingBlock.value.unit = value
    }
  )

  const debouncedStoreUpdate = useDebounceFn(async (nextBlock: BuildingBlockDto) => {
    await run(() => buildingBlockStore.update(nextBlock), block.value.id)
  }, DEFAULT_DEBOUNCE_DELAY)

  const updateBlock = () => {
    if (props.readonly || isEditLockedByMissingIssue.value) return
    if (
      editingBlock.value.name === block.value.name &&
      editingBlock.value.unit === block.value.unit
    ) {
      return
    }

    debouncedStoreUpdate({
      ...block.value,
      name: editingBlock.value.name,
      unit: editingBlock.value.unit
    })
  }

  const onNameTyping = (event: Event) => {
    const inputElement = event.target instanceof HTMLInputElement ? event.target : null
    editingBlock.value.name = inputElement?.value ?? ''
    updateBlock()
  }

  const onUnitTyping = (event: Event) => {
    const inputElement = event.target instanceof HTMLInputElement ? event.target : null
    editingBlock.value.unit = inputElement?.value ?? ''
    updateBlock()
  }

  const onAddProductSelected = async (product: ProductDto) => {
    if (props.readonly || isEditLockedByMissingIssue.value) return
    const newMaterialPayload = {
      product: product.id,
      buildingblock: block.value.id,
      lifetime: product.lifetime,
      conversion_factor: DEFAULT_CONVERSION_FACTOR,
      impact_unit: product.impact_unit
    }
    await materialStore.create(newMaterialPayload as unknown as MaterialDto)
  }

  const onAddEquipmentRow = async () => {
    if (props.readonly || isEditLockedByMissingIssue.value) return
    await materialStore.create({
      buildingblock: block.value.id,
      name: blockTypeCopy.value.newRowName,
      lifetime: PERMANENT_LIFETIME
    } as unknown as MaterialDto)
  }

  const onDuplicateBlock = async () => {
    if (props.readonly || isEditLockedByMissingIssue.value) return
    await buildingBlockStore.duplicateBuildingBlock(block.value)
    await Promise.all([
      materialStore.fetchItems({ block__calculation: calculationId.value }),
      activityStore.fetchItems({ material__block__calculation: calculationId.value }),
      buildingBlockStore.fetchItems({ calculation: calculationId.value })
    ])
  }

  const onDeleteBlock = async () => {
    if (props.readonly) return

    const blockId = block.value.id

    await quantityBuildingBlockStore.fetchItems({ calculation: calculationId.value })

    const linkedDesignLinks = quantityBuildingBlockStore.items.filter(
      (quantity) => quantity.building_block === blockId
    )
    const linkedTransportConfigs = transportConfigStore.items.filter(
      (config) => config.building_block === blockId
    )

    let message: string | undefined
    if (linkedDesignLinks.length || linkedTransportConfigs.length) {
      const parts: string[] = []

      if (linkedDesignLinks.length) {
        const names = linkedDesignLinks
          .map((quantity) => quantity.alias || quantity.name)
          .join(', ')
        parts.push(`${linkedDesignLinks.length} design link row(s) (${names})`)
      }

      if (linkedTransportConfigs.length) {
        parts.push(`${linkedTransportConfigs.length} transport configuration(s)`)
      }

      message =
        `This building block is linked to ${parts.join(' and ')}. Deleting it will remove those ` +
        `links and can change the calculation result. This action cannot be undone.`
    }

    const confirmed = await confirmDeleteStore.confirm({
      name: block.value.name,
      ...(message ? { message } : {})
    })
    if (!confirmed) return

    await deleteBuildingBlock(block.value, calculationId.value)
  }

  const canDeleteMaterial = (materialId: number) => {
    if (props.readonly) return false
    if (!isEditLockedByMissingIssue.value) return true
    return missingMaterialIds.value.has(Number(materialId))
  }

  return {
    materialItems,
    blockMaterials,
    blockType,
    isEquipmentBlock,
    impactLabel,
    autosaveMessage,
    autosaveMessageColor,
    blockTotal,
    issueClass,
    issueBadgeColor,
    issueTitle,
    isEditLockedByMissingIssue,
    missingDetails,
    changedDetails,
    showAddProduct,
    blockTypeCopy,
    buildingBlockNameField,
    buildingBlockUnitField,
    editingBlock,
    onNameTyping,
    onUnitTyping,
    onAddProductSelected,
    onAddEquipmentRow,
    onDuplicateBlock,
    onDeleteBlock,
    canDeleteMaterial
  }
}
